import { useMemo } from 'react';
import { Select } from '@/components/ui/Select';
import { Field } from '@/components/ui/Field';
import { Alert } from '@/components/ui/Alert';
import { Spinner } from '@/components/ui/Feedback';
import { ScopePicker } from './ScopePicker';
import { scopeSelectionGap } from './scope-selection';
import { resolveGrant, type GrantFormState } from './grant-state';
import { GrantPreviewPanel } from './GrantPreviewPanel';
import {
  describeScope,
  previewGrant,
  type GrantPreview,
  type HierarchySnapshot,
} from './effective-permissions';
import type { Organization, Role, RoleAssignment } from '@/lib/api/types';

/*
  The role, scope and expiry half of a grant, shared by the invite dialog and the
  add-an-assignment dialog. Both are the same decision: (role, scope, optional expiry). Only the
  subject differs, so only the subject is duplicated.

  The expiry field is a plain datetime-local input rather than a date picker component. A native
  input is keyboard accessible for free, respects the operating system's locale and 12 or 24 hour
  preference, and is the control Windows users already know. A custom picker would be four
  hundred lines to reimplement that badly.

  Expiry is worth offering prominently because a time-boxed grant is the safest version of most
  elevations: "on call tonight" is a nine hour assignment, not a permanent one, and the fixture
  data has exactly that case on Owen Barclay.
*/

export interface GrantFormProps {
  readonly state: GrantFormState;
  readonly onChange: (next: GrantFormState) => void;
  /** Who the grant is for, used in the preview copy. */
  readonly subject: string;
  readonly roles: readonly Role[];
  readonly organizations: readonly Organization[];
  readonly hierarchy: HierarchySnapshot;
  /** The signed-in user's own assignments, for the no-escalation check. */
  readonly grantorAssignments: readonly RoleAssignment[];
  readonly loading: boolean;
  readonly roleError?: string | undefined;
  readonly scopeError?: string | undefined;
  readonly expiresAtError?: string | undefined;
}

export function GrantForm({
  state,
  onChange,
  subject,
  roles,
  organizations,
  hierarchy,
  grantorAssignments,
  loading,
  roleError,
  scopeError,
  expiresAtError,
}: GrantFormProps): React.JSX.Element {
  const organizationNames = useMemo(
    () => new Map(organizations.map((org) => [org.id, org.name])),
    [organizations],
  );

  const resolved = resolveGrant(state, roles);
  const gap = scopeSelectionGap(state.scope);

  const preview: GrantPreview | null =
    resolved === null
      ? null
      : previewGrant(resolved.role, resolved.scope, grantorAssignments, roles, hierarchy);

  const selectedRole = roles.find((candidate) => candidate.id === state.roleId);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Select
          label="Role"
          value={state.roleId ?? '__unset__'}
          options={[
            { value: '__unset__', label: 'Choose a role' },
            ...roles.map((role) => ({
              value: role.id,
              label: role.isSystem ? role.name : `${role.name} (custom)`,
            })),
          ]}
          onValueChange={(next) => {
            onChange({ ...state, roleId: next === '__unset__' ? null : next });
          }}
        />
        {selectedRole?.description === undefined || selectedRole.description === null ? null : (
          <p className="mt-1.5 text-xs text-fg-subtle">{selectedRole.description}</p>
        )}
        {roleError === undefined ? null : (
          <p role="alert" className="mt-1 text-xs font-medium text-[var(--color-danger)]">
            {roleError}
          </p>
        )}
      </div>

      <ScopePicker
        selection={state.scope}
        onChange={(scope) => {
          onChange({ ...state, scope });
        }}
        organizations={organizations}
        sites={hierarchy.sites}
        groups={hierarchy.groups}
        devices={hierarchy.devices}
      />
      {scopeError === undefined ? null : (
        <p role="alert" className="text-xs font-medium text-[var(--color-danger)]">
          {scopeError}
        </p>
      )}

      <Field
        type="datetime-local"
        label="Expires"
        value={state.expiresAtLocal}
        hint="Optional. Leave it empty for a permanent grant. A time-boxed assignment lapses on its own, which is the safest way to hand out an elevation for one night."
        onChange={(event) => {
          onChange({ ...state, expiresAtLocal: event.target.value });
        }}
        {...(expiresAtError === undefined ? {} : { error: expiresAtError })}
      />

      {loading ? (
        <Spinner label="Loading roles and the organization hierarchy" />
      ) : preview === null ? (
        <Alert tone="info" title="Nothing to preview yet">
          {state.roleId === null
            ? 'Choose a role, then a scope, and the exact permissions this grant confers will be listed here before you confirm.'
            : (gap ?? 'Finish choosing the scope.')}
        </Alert>
      ) : (
        <GrantPreviewPanel
          preview={preview}
          hierarchy={hierarchy}
          scopeLabel={describeScope(preview.scope, hierarchy, organizationNames)}
          subject={subject}
        />
      )}
    </div>
  );
}
